import { format } from 'date-fns'
import { Calendar, Pencil, Shield, Trash2 } from 'lucide-react'

import { useRolesTable } from '@/hooks/features/roles'
import { useDataTable } from '@/hooks/ui'

import { Badge } from '@/components/ui/badge'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { ColumnVisibilityDropdown, DataTable, NoData } from '@/components/ui/data-table'
import { Pagination } from '@/components/ui/pagination-controls'

import { ActionDropdown, ConfirmDialog, PageHeader } from '@/components/atoms'

import { RoleModel } from '@/types'

import { RoleDialog } from './RoleDialog'

import { ScrollArea, ScrollBar } from '@/components/ui/scroll-area'

type TableColumns = 'name' | 'key' | 'permissions' | 'createdAt' | 'actions'

const DEFAULT_VISIBLE_COLUMNS: TableColumns[] = [
  'name',
  'key',
  'permissions',
  'createdAt',
  'actions',
]

export const RolesTable = () => {
  const {
    loading,
    data,
    pagination,
    mode,
    dialogOpen,
    editRole,
    deleteDialogOpen,
    roleToDelete,
    createLoading,
    updateLoading,
    deleteLoading,
    setPage,
    setPageSize,
    openCreateDialog,
    openEditDialog,
    closeDialog,
    handleDeleteClick,
    handleDeleteConfirm,
    setDeleteDialogOpen,
    handleCreateRole,
    handleUpdateRole,
  } = useRolesTable()

  const { columns, changeColumnVisibility, disabledColumns } = useDataTable<
    RoleModel.Role,
    TableColumns
  >({
    columns: [
      {
        accessorKey: 'name',
        columnLabel: () => 'Role',
        cell: (data) => (
          <div className="flex items-center gap-3">
            <div className="flex size-8 shrink-0 items-center justify-center rounded-md bg-fill">
              <Shield className="size-4 text-ink" />
            </div>
            <div className="font-medium text-ink">{data.name}</div>
          </div>
        ),
        hasSorting: true,
      },
      {
        accessorKey: 'key',
        columnLabel: () => 'Key',
        cell: (data) => (
          <code className="rounded-sm bg-subdued px-1.5 py-0.5 font-mono text-xs text-text-2">{data.key}</code>
        ),
      },
      {
        accessorKey: 'permissions',
        columnLabel: () => 'Permissions',
        cell: (data) => (
          <div className="flex items-center gap-2">
            <Badge tone="soft" className="tabular-nums">
              {data.permissions?.length || 0} permissions
            </Badge>
            {data.permissions?.length > 0 && (
              <span className="max-w-40 truncate text-sm text-text-2">
                {data.permissions.slice(0, 3).join(', ')}...
              </span>
            )}
          </div>
        ),
      },
      {
        accessorKey: 'createdAt',
        columnLabel: () => 'Created',
        cell: (data) => (
          <div className="flex items-center gap-1.5 text-sm tabular-nums text-text-2">
            <Calendar className="size-4" />
            {data.createdAt ? format(new Date(data.createdAt), 'MMM d, yyyy') : 'N/A'}
          </div>
        ),
        hasSorting: true,
      },
      {
        accessorKey: 'actions',
        columnLabel: () => 'Actions',
        cell: (data) => (
          <div className="flex justify-end">
            <ActionDropdown
              items={[
                {
                  label: 'Edit permissions',
                  icon: Pencil,
                  onClick: () => openEditDialog(data),
                },
                {
                  label: 'Delete role',
                  icon: Trash2,
                  variant: 'destructive',
                  onClick: () => handleDeleteClick(data),
                },
              ]}
            />
          </div>
        ),
        classes: () => ({
          column: 'text-right',
          cell: 'text-right',
        }),
        isAction: true,
      },
    ],
    visibleColumns: DEFAULT_VISIBLE_COLUMNS,
    disabledColumns: ['actions'],
  })

  return (
    <div>
      <PageHeader
        size="large"
        title="Roles and permissions"
        onAddClick={openCreateDialog}
        addButtonText="Create role"
      />

      <Card>
        <CardHeader>
          <div className="flex flex-col justify-between gap-3 lg:flex-row lg:items-center">
            <div className="space-y-1">
              <CardTitle>Roles</CardTitle>
              <CardDescription className="text-text-2">
                Roles define what staff can do in the dashboard. {pagination.totalCount}{' '}
                {pagination.totalCount === 1 ? 'role' : 'roles'}
                {data.length > 0 &&
                  `, ${Math.round(
                    data.reduce((acc, r) => acc + (r.permissions?.length || 0), 0) / data.length
                  )} permissions on average`}
                .
              </CardDescription>
            </div>
            <div className="flex items-center justify-between gap-2 sm:justify-end">
              <Pagination.PageSizeDropdown
                pageSize={pagination.pageSize}
                onPageSizeChange={setPageSize}
              />
              <ColumnVisibilityDropdown
                columns={columns}
                onColumnVisibilityChange={changeColumnVisibility}
                disabledColumns={disabledColumns}
              />
            </div>
          </div>
        </CardHeader>
        <CardContent>
          {!loading && data.length === 0 ? (
            <NoData
              title="No roles found"
              btnText="Create role"
              openCreateDialog={openCreateDialog}
            />
          ) : (
            <>
              <ScrollArea className="w-full">
                <div className="inline-block min-w-full align-middle">
                  <DataTable columns={columns} data={data} loading={loading} />
                </div>
                <ScrollBar orientation="horizontal" />
              </ScrollArea>

              <Pagination.Buttons
                currentPage={pagination.currentPage}
                totalItems={pagination.totalCount}
                pageSize={pagination.pageSize}
                onPageChange={setPage}
              />
            </>
          )}
        </CardContent>
      </Card>

      <RoleDialog
        open={dialogOpen}
        mode={mode}
        editRole={editRole}
        closeDialog={closeDialog}
        onCreate={handleCreateRole}
        onUpdate={handleUpdateRole}
        loading={createLoading || updateLoading}
      />

      <ConfirmDialog
        open={deleteDialogOpen}
        onOpenChange={setDeleteDialogOpen}
        title="Delete role?"
        description={`This will permanently delete the role "${roleToDelete?.name}". Active users with this role may lose access to certain features.`}
        confirmText={deleteLoading ? 'Deleting...' : 'Delete'}
        onConfirm={handleDeleteConfirm}
        loading={deleteLoading}
        variant="destructive"
      />
    </div>
  )
}
